import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client';

import DeleteModal from '@atom/components/common/DeleteModal';
import UserGroupIcon from '@atom/components/common/UserGroupIcon';
import EditTaskUserGroupModal from '@atom/components/common/workOrderDetail/editTaskUserGroupModal/EditTaskUserGroupModal';
import TaskContext from '@atom/components/workOrderPreview/taskView/TaskContext';
import WorkOrderPreviewContext from '@atom/components/workOrderPreview/WorkOrderPreviewContext';
import { TASK_USER_GROUP_REMOVE } from '@atom/graph/task';
import { Icon, IconButton } from '@atom/mui';
import {
  Task,
  TaskUserGroupItem,
  TaskUserGroupRemoveInput,
} from '@atom/types/task';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';

import classes from './teamSection.module.css';

interface Props {
  userGroup: TaskUserGroupItem;
}

const TaskUserGroupTile = ({ userGroup }: Props) => {
  const { task } = useContext(TaskContext);
  const { workOrderDetail, refetch } = useContext(WorkOrderPreviewContext);

  const [open, setOpen] = useState<boolean>(false);
  const [removeModalOpen, setRemoveModalOpen] = useState<boolean>(false);

  const [taskUserGroupRemove] = useMutation<
    { taskUserGroupRemove: Task },
    { input: TaskUserGroupRemoveInput }
  >(TASK_USER_GROUP_REMOVE, {
    onCompleted: () => {
      refetch();
    },
  });

  const isEditDisabled =
    workOrderDetail.isClosed || doesNotHaveRolePermissions(ROLE_SETS.MANAGER);

  const removeUserGroup = async () => {
    await taskUserGroupRemove({
      variables: {
        input: {
          id: task.id,
          userGroupId: userGroup.userGroupId,
          workOrderId: workOrderDetail.id,
        },
      },
    });

    setRemoveModalOpen(false);
    refetch();
  };

  const usersLabel = userGroup.quantity === 1 ? 'User' : 'Users';

  return (
    <>
      <div className={classes.userContainer}>
        <div
          className={classes.userContentContainer}
          onClick={() => setOpen(!workOrderDetail.isClosed)}
        >
          <UserGroupIcon colorId={userGroup.colorId} />
          <div className={classes.userDetails}>
            <div className={classes.name}>{userGroup.name}</div>
            <div className={classes.email}>
              <div className={classes.email}>
                <span>{`${userGroup.userCount} ${usersLabel}`}</span>
              </div>
            </div>
          </div>
        </div>
        {!isEditDisabled && (
          <IconButton
            onClick={() => setRemoveModalOpen(true)}
            tooltip="Remove"
            edge="end"
          >
            <Icon>close</Icon>
          </IconButton>
        )}
      </div>
      <EditTaskUserGroupModal
        open={open}
        onClose={() => setOpen(false)}
        userGroupId={userGroup.userGroupId}
        quantity={userGroup.quantity}
        workTime={userGroup?.workTime}
        selectedBudgetId={userGroup?.budget?.budgetId}
        workOrderDetail={workOrderDetail}
        task={task}
        refetch={refetch}
      />
      <DeleteModal
        open={removeModalOpen}
        onCancel={() => setRemoveModalOpen(false)}
        onConfirm={removeUserGroup}
        title="Remove User Group From Task"
        confirmText="Remove"
        content={
          <div>
            Are you sure you want to remove this user group from the task?
          </div>
        }
      />
    </>
  );
};

export default TaskUserGroupTile;
